<template>
  <!--
    WHAT: The DataAttribute join-graph editor — the SQL plumbing behind an
          artifact. Renders the step chain (DAs joined top→bottom), per-step join
          clauses, output columns + optional output-contract checklist, the
          combined-SQL preview, an "Add attribute" library drawer, and (via the
          PrimitiveEditorDrawer) DA edit/create.
    WHERE: [reused verbatim from v1] Wrapped by JoinGraphSection.vue, which is the
           shared body for EVERY "Data Source" detail tab (metric / flag /
           worklist / insight). JoinGraphSection translates this component's
           v-model ⇄ the section `patch` contract.
    CONTRACT:
      props:
        modelValue (Object, req)   — { steps:[{da_id, role, join_type,
                                       join_on_sql}], primary_key_columns:[...] }
        attributes (Array)         — all DataAttributes for the library
        columnsCache (Object)      — { da_id: [columns] } (host-owned)
        sql (String)               — combined-SQL preview text (host-supplied)
        configTabLabel (String)    — if set, shows a "config" tab (via #config slot)
        excludedColumns (Array)    — output columns toggled off (v-model:excludedColumns)
        showContract (Boolean)     — show "Ready to run" badge + output-contract checklist
      slots: #config, #above-sql (both receive { output-columns, pk-columns })
      emits: update:modelValue, update:excludedColumns, request-columns,
             refresh-sql, attribute-saved
    NOTES: [reused verbatim from v1] shared with other callers — do NOT restyle
           without checking them. Gotchas: the host owns columnsCache + sql and
           must respond to `request-columns` / `refresh-sql`; join clauses are
           edited as one string ("<JOIN_TYPE> ON <predicate>") then parsed back
           into join_type + join_on_sql; step aliases are positional (a, b, c…).
  -->
  <div class="ms-builder">
    <!-- ============ MAIN GRID ============ -->
    <div class="grid items-stretch gap-5" style="grid-template-columns:minmax(0,1fr) clamp(384px, 32%, 560px);">

      <!-- ---------- BUILD ---------- -->
      <section class="bg-white border border-gray-200 rounded-2xl px-6 py-5">
        <div class="flex items-start justify-between gap-3.5 mb-4">
          <div>
            <div class="text-[15px] font-bold tracking-tight text-gray-800">Build</div>
            <div class="text-xs text-gray-400 mt-0.5">{{ nodes.length }} attribute{{ nodes.length === 1 ? '' : 's' }} joined top to bottom — click a step to edit its join</div>
          </div>
          <button
            class="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3.5 py-2 hover:bg-blue-100 flex-shrink-0"
            @click="libraryOpen = true"
          >＋ Add attribute</button>
        </div>

        <div
          ref="canvas"
          class="flex flex-col"
          :class="{ 'rounded-xl ring-2 ring-blue-200': dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @drop="onDrop"
        >
          <template v-for="(node, i) in nodes" :key="node.da_id">
            <!-- connector / join chip -->
            <div v-if="i > 0" class="flex items-center gap-3 py-1.5 pl-[21px]">
              <div class="flex-shrink-0" style="width:2px;height:24px;background:repeating-linear-gradient(180deg,#D1D5DB 0 4px,transparent 4px 8px);"></div>
              <button
                class="inline-flex items-center gap-2 max-w-full rounded-lg px-2.5 py-1 border"
                :style="expandedStep === i
                  ? { background:'#EFF6FF', borderColor:'#BFDBFE' }
                  : { background:'#F9FAFB', borderColor:'#E5E7EB' }"
                @click="toggleStep(i)"
              >
                <span class="font-mono text-[11px] text-gray-500 truncate" style="max-width:440px;">{{ joinSummary(node) }}</span>
                <span class="text-[11px] text-gray-400 flex-shrink-0">{{ expandedStep === i ? '▾' : '▸' }}</span>
              </button>
            </div>

            <!-- step card row -->
            <div class="flex items-stretch gap-2">
              <button
                class="flex-1 min-w-0 flex items-center gap-3.5 text-left border rounded-xl px-4 py-3.5"
                :style="{
                  background: expandedStep === i ? rm(node).bg : '#FFFFFF',
                  borderColor: expandedStep === i ? rm(node).bd : '#E5E7EB',
                  borderLeft: '4px solid ' + rm(node).c,
                }"
                @click="toggleStep(i)"
              >
                <span
                  class="inline-flex items-center justify-center rounded-lg text-white font-mono text-[13px] font-semibold flex-shrink-0"
                  style="width:30px;height:30px;"
                  :style="{ background: rm(node).c }"
                >{{ alias(i) }}</span>
                <div class="min-w-0 flex-1">
                  <span class="block text-[15px] font-semibold text-gray-800 truncate">{{ attributesById[node.da_id]?.name || ('#' + node.da_id) }}</span>
                  <div class="flex items-center gap-2.5 mt-0.5">
                    <span class="text-[11.5px] font-medium" :style="{ color: rm(node).c }">{{ rm(node).label }}</span>
                    <span class="font-mono text-[11.5px] text-gray-400">#{{ node.da_id }}</span>
                    <span class="font-mono text-[11.5px] text-gray-400">{{ (columnsCache[node.da_id] || []).length }} cols</span>
                  </div>
                </div>
                <span class="text-[13px] text-gray-300 flex-shrink-0">{{ expandedStep === i ? '▾' : '▸' }}</span>
              </button>
              <button
                class="w-9 flex-shrink-0 flex items-center justify-center bg-white border border-gray-200 rounded-xl text-gray-300 text-[13px] hover:text-blue-500 hover:border-blue-200"
                title="Edit this attribute"
                @click.stop="editAttribute(node.da_id)"
              >✎</button>
              <button
                class="w-9 flex-shrink-0 flex items-center justify-center bg-white border border-gray-200 rounded-xl text-gray-300 text-[15px] hover:text-red-400 hover:border-red-200"
                title="Remove"
                @click="removeNode(node.da_id)"
              >×</button>
            </div>

            <!-- expanded editor -->
            <div
              v-if="expandedStep === i"
              class="ms-rise mt-2 mb-2 ml-[21px] px-4.5 py-4 bg-gray-50 border border-gray-200 rounded-xl"
              style="padding-left:18px;padding-right:18px;"
            >
              <template v-if="i > 0">
                <div class="text-xs font-semibold text-gray-600 mb-1.5">Join clause</div>
                <textarea
                  :value="joinClause(node)"
                  rows="4"
                  class="w-full font-mono text-xs leading-relaxed text-gray-800 bg-white border border-gray-200 rounded-lg px-3 py-2.5 resize-y mb-3"
                  placeholder="LEFT JOIN ON b.BENE_MBI_ID = a.BENE_MBI_ID"
                  @input="setJoinClause(i, $event.target.value)"
                ></textarea>
                <button
                  class="flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-600 mb-1.5"
                  @click.stop="refsOpen = refsOpen === i ? null : i"
                >
                  <span class="text-[10px]">{{ refsOpen === i ? '▾' : '▸' }}</span>
                  Insert column reference
                </button>
                <div v-if="refsOpen === i" class="flex flex-wrap gap-1 mb-4">
                  <button
                    v-for="(ref, ri) in refChips(i)"
                    :key="ri"
                    class="font-mono text-[10.5px] text-gray-700 bg-white border border-gray-200 rounded-md px-1.5 py-0.5 hover:bg-gray-50"
                    @click="insertRef(i, ref.alias + '.' + ref.col)"
                  ><span class="font-semibold" :style="{ color: ref.c }">{{ ref.alias }}</span>.{{ ref.col }}</button>
                </div>
                <div v-else class="mb-4"></div>
              </template>
              <div class="text-xs font-semibold text-gray-600 mb-1.5">Columns contributed</div>
              <div class="flex flex-wrap gap-1 mb-4">
                <span
                  v-for="col in (columnsCache[node.da_id] || [])"
                  :key="col"
                  class="font-mono text-[11px] text-gray-700 bg-white border border-gray-200 rounded-md px-2 py-0.5"
                >{{ col }}</span>
                <span v-if="!(columnsCache[node.da_id] || []).length" class="text-[11px] text-gray-400">Loading columns…</span>
              </div>
              <div class="flex items-center justify-between mb-1.5">
                <span class="text-xs font-semibold text-gray-600">Attribute SQL</span>
                <button class="font-mono text-[10px] text-gray-400 hover:text-gray-600" @click.stop="copyDaSql(node.da_id)">Copy</button>
              </div>
              <pre
                class="ms-scroll m-0 p-3 font-mono text-[11px] leading-relaxed text-gray-700 bg-white border border-gray-200 rounded-lg overflow-auto whitespace-pre"
                style="max-height:280px;"
              >{{ sqlCache[node.da_id] === undefined ? 'Loading SQL…' : sqlCache[node.da_id] }}</pre>
            </div>
          </template>

          <div v-if="!nodes.length" class="text-center text-xs text-gray-400 py-10 border border-dashed border-gray-200 rounded-xl">
            No attributes yet. Click “Add attribute” or drag one in.
          </div>
        </div>
      </section>

      <!-- ---------- CONFIG (tabbed) ---------- -->
      <aside class="bg-white border border-gray-200 rounded-2xl overflow-hidden sticky" style="top:18px;">
        <div class="flex border-b border-gray-200">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="flex-1 relative text-[13px] font-semibold py-3.5"
            :style="{ color: rightTab === t.key ? '#1F2937' : '#9CA3AF' }"
            @click="rightTab = t.key"
          >
            {{ t.label }}
            <span class="absolute left-4 right-4 h-0.5 rounded" style="bottom:-1px;" :style="{ background: rightTab === t.key ? '#2563EB' : 'transparent' }"></span>
          </button>
        </div>

        <!-- OUTPUT -->
        <div v-show="rightTab === 'output'">
          <div v-if="showContract" class="px-4.5 pt-4.5 pb-4 border-b border-gray-200" style="padding-left:18px;padding-right:18px;padding-top:18px;">
            <div class="flex items-center gap-2.5 mb-3">
              <span
                class="inline-flex items-center justify-center rounded-xl text-[15px] font-bold flex-shrink-0"
                style="width:34px;height:34px;"
                :style="{ background: ready.bg, color: ready.dot }"
              >{{ ready.icon }}</span>
              <div class="min-w-0">
                <div class="text-[14.5px] font-bold" :style="{ color: ready.tx }">{{ ready.label }}</div>
                <div class="text-xs text-gray-400 mt-px">{{ ready.detail }}</div>
              </div>
            </div>
            <div class="rounded-md overflow-hidden" style="height:7px;background:#F3F4F6;">
              <div class="h-full rounded-md" :style="{ width: ready.pct + '%', background: ready.dot }"></div>
            </div>
          </div>
          <div class="px-4.5 pt-3.5 pb-1.5 flex items-center justify-between" style="padding-left:18px;padding-right:18px;">
            <span class="text-xs font-semibold text-gray-600">Output columns</span>
            <span class="font-mono text-[11px] text-gray-400">{{ activeColCount }}/{{ allOutputColumns.length }}</span>
          </div>
          <div class="ms-scroll overflow-y-auto pb-2" style="max-height:188px;">
            <div
              v-for="col in allOutputColumns"
              :key="col.col + col.da_id"
              class="flex items-center gap-2.5 px-4.5 py-1.5 cursor-pointer"
              style="padding-left:18px;padding-right:18px;"
              :style="{ opacity: isExcluded(col.col) ? 0.45 : 1 }"
              @click="toggleColumn(col.col)"
            >
              <span
                class="inline-flex items-center justify-center rounded text-white text-[10px] flex-shrink-0"
                style="width:15px;height:15px;border:1.5px solid;"
                :style="isExcluded(col.col)
                  ? { borderColor:'#D1D5DB', background:'#fff' }
                  : { borderColor:'#2563EB', background:'#2563EB' }"
              >{{ isExcluded(col.col) ? '' : '✓' }}</span>
              <span class="font-mono text-[9.5px] text-gray-400">#{{ col.da_id }}</span>
              <span class="font-mono text-xs text-gray-700 flex-1" :class="{ 'line-through': isExcluded(col.col) }">{{ col.col }}</span>
              <button
                type="button"
                class="text-[9.5px] font-medium rounded px-1.5 py-0.5 border flex-shrink-0"
                :class="isIdentifier(col.col)
                  ? 'text-blue-700 bg-blue-50 border-blue-200'
                  : 'text-gray-400 bg-white border-gray-200 hover:text-blue-600'"
                @click.stop="toggleIdentifier(col.col)"
              >{{ isIdentifier(col.col) ? 'Identifier' : '+ Identifier' }}</button>
            </div>
            <div v-if="!allOutputColumns.length" class="px-4.5 py-2 text-xs text-gray-400" style="padding-left:18px;">No attributes on canvas</div>
          </div>
          <template v-if="showContract">
            <div class="px-4.5 pt-3 pb-1.5 border-t border-gray-200" style="padding-left:18px;padding-right:18px;">
              <span class="text-xs font-semibold text-gray-600">Output contract</span>
            </div>
            <div class="ms-scroll overflow-y-auto pt-0.5 pb-3" style="max-height:212px;">
              <div v-for="c in contractChecks" :key="c.col" class="flex items-center gap-2.5 px-4.5 py-1" style="padding-left:18px;padding-right:18px;">
                <span class="font-mono text-xs w-3" :style="{ color: c.iconColor }">{{ c.icon }}</span>
                <span class="font-mono text-[11.5px]" :style="{ color: c.txColor }">{{ c.col }}</span>
                <span class="text-[10px] text-gray-400 ml-auto">{{ c.reason }}</span>
              </div>
            </div>
          </template>
        </div>

        <!-- HOST CONFIG TAB (slot) -->
        <div v-show="rightTab === 'config'" class="p-4.5" style="padding:18px;">
          <slot name="config" :output-columns="allOutputColumns" :pk-columns="pkColumns" />
        </div>
      </aside>
    </div>

    <!-- ============ FULL-WIDTH SLOT (above Combined SQL) ============ -->
    <div v-if="$slots['above-sql']" class="mt-5">
      <slot name="above-sql" :output-columns="allOutputColumns" :pk-columns="pkColumns" />
    </div>

    <!-- ============ FULL-WIDTH SQL (beneath both panels) ============ -->
    <section class="bg-white border border-gray-200 rounded-2xl overflow-hidden mt-5">
      <div class="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <span class="text-[13px] font-bold tracking-tight text-gray-800">Combined SQL</span>
        <div class="flex items-center gap-3.5">
          <button class="font-mono text-[11px] text-gray-400 hover:text-gray-600" @click="$emit('refresh-sql')">Refresh</button>
          <button class="font-mono text-[11px] text-blue-600 hover:text-blue-700" @click="copySql">Copy</button>
        </div>
      </div>
      <pre class="ms-scroll m-0 p-4 font-mono text-[11.5px] leading-relaxed text-gray-700 overflow-x-auto whitespace-pre" style="max-height:640px;">{{ sql }}</pre>
    </section>

    <!-- ============ ADD ATTRIBUTE LIBRARY DRAWER ============ -->
    <div v-if="libraryOpen" class="fixed inset-0 z-50">
      <div class="absolute inset-0" style="background:rgba(17,24,39,0.30);" @click="libraryOpen = false"></div>
      <aside class="ms-drawer absolute top-0 right-0 h-full flex flex-col" style="width:520px;max-width:100%;background:#F9FAFB;box-shadow:-12px 0 40px rgba(17,24,39,0.12);">
        <header class="px-5 py-4.5 border-b border-gray-200 bg-white flex items-center justify-between" style="padding-top:18px;padding-bottom:18px;">
          <div>
            <div class="text-[15px] font-bold text-gray-800">Add data attribute</div>
            <div class="text-xs text-gray-400 mt-px">Joins onto the bottom of the pipeline</div>
          </div>
          <div class="flex items-center gap-2">
            <button
              class="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5 hover:bg-blue-100"
              @click="createAttribute"
            >＋ Create new</button>
            <button class="text-xl text-gray-400 hover:text-gray-600 leading-none" @click="libraryOpen = false">×</button>
          </div>
        </header>
        <div class="px-4.5 py-3 bg-white border-b border-gray-200 flex flex-wrap gap-1.5" style="padding-left:18px;padding-right:18px;">
          <button
            v-for="f in railFilters"
            :key="f.role"
            class="text-[11.5px] font-medium rounded-full px-2.5 py-1 border"
            :style="railFilter === f.role
              ? { color:'#fff', background: f.activeBg, borderColor: f.activeBg }
              : { color: f.tx, background: f.bg, borderColor: f.bd }"
            @click="railFilter = f.role"
          >{{ f.label }}</button>
        </div>
        <div class="ms-scroll flex-1 overflow-y-auto px-3.5 py-2.5">
          <button
            v-for="p in libraryItems"
            :key="p.id"
            class="w-full text-left flex items-center gap-2.5 px-3.5 py-3 rounded-xl mb-1.5 border border-gray-200"
            :style="{ background: p.onCanvas ? '#F3F4F6' : '#FFFFFF', opacity: p.onCanvas ? 0.6 : 1, cursor: p.onCanvas ? 'default' : 'pointer' }"
            @click="!p.onCanvas && addAttribute(p.id)"
          >
            <span class="rounded flex-shrink-0" style="width:9px;height:9px;" :style="{ background: rm(p).c }"></span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="font-mono text-[11px] text-gray-400">#{{ p.id }}</span>
                <span class="text-[13.5px] font-medium text-gray-800">{{ p.name }}</span>
              </div>
              <div class="text-[11.5px] text-gray-500 mt-px truncate">{{ rm(p).label }}<span v-if="p.description"> · {{ p.description }}</span></div>
            </div>
            <span class="text-xs font-semibold flex-shrink-0" :style="{ color: p.onCanvas ? '#9CA3AF' : '#2563EB' }">{{ p.onCanvas ? 'in pipeline' : '＋ add' }}</span>
          </button>
          <div v-if="!libraryItems.length" class="text-xs text-gray-400 px-2 py-3">No primitives for this filter.</div>
        </div>
      </aside>
    </div>

    <!-- ============ DA EDITOR DRAWER (edit existing / create new) ============ -->
    <PrimitiveEditorDrawer
      v-if="editorOpen"
      :key="editorKey"
      :attribute-id="editorDaId"
      @close="editorOpen = false"
      @saved="onAttributeSaved"
    />
  </div>
</template>

<script>
import axios from 'axios';
import PrimitiveEditorDrawer from '@/components/Admin/MetricStudio/drawers/PrimitiveEditorDrawer.vue';

export const ROLES = {
  date_spine: { label: 'Date Spine', short: 'Date', c: '#A855F7', bg: '#FAF5FF', bd: '#E9D5FF', tx: '#7E22CE' },
  population: { label: 'Population', short: 'Pop', c: '#3B82F6', bg: '#EFF6FF', bd: '#BFDBFE', tx: '#1D4ED8' },
  denominator: { label: 'Denominator', short: 'Denom', c: '#F59E0B', bg: '#FFFBEB', bd: '#FDE68A', tx: '#B45309' },
  intervention: { label: 'Intervention', short: 'Interv', c: '#10B981', bg: '#ECFDF5', bd: '#A7F3D0', tx: '#047857' },
  fact: { label: 'Fact', short: 'Fact', c: '#0EA5E9', bg: '#F0F9FF', bd: '#BAE6FD', tx: '#0369A1' },
  metadata: { label: 'Metadata', short: 'Meta', c: '#6B7280', bg: '#F3F4F6', bd: '#E5E7EB', tx: '#4B5563' },
  eagle_eye: { label: 'Eagle Eye', short: 'Eagle', c: '#6B7280', bg: '#F3F4F6', bd: '#E5E7EB', tx: '#4B5563' },
  cost_utilization: { label: 'Cost / Util', short: 'Cost', c: '#6B7280', bg: '#F3F4F6', bd: '#E5E7EB', tx: '#4B5563' },
  '': { label: 'No Category', short: '—', c: '#9CA3AF', bg: '#F3F4F6', bd: '#E5E7EB', tx: '#4B5563' },
};

export default {
  name: 'StudioJoinBuilder',
  components: { PrimitiveEditorDrawer },
  props: {
    // v-model: { steps:[{da_id, role, join_type, join_on_sql}], primary_key_columns:[...] }
    modelValue: { type: Object, required: true },
    attributes: { type: Array, default: () => [] },
    columnsCache: { type: Object, default: () => ({}) },
    sql: { type: String, default: '' },
    // extra config tab label; when set, a "config" tab is shown between Output and SQL
    configTabLabel: { type: String, default: '' },
    // excluded output columns (v-model:excludedColumns)
    excludedColumns: { type: Array, default: () => [] },
    // show the "Ready to run" badge + Output-contract checklist (metric shape)
    showContract: { type: Boolean, default: false },
    // calculation grain: 'npi' (claims track, default) or 'entity'. Drives which
    // output-contract checklist is shown — entity-grain DAs emit ENTITY_ID/YEAR/
    // OBSERVATION_DATE, not the NPI/patient/window columns.
    grain: { type: String, default: 'npi' },
    // { required:[...], optional:[...] } from the backend (studio-sql returns
    // output_contract, built from the engine's own GROUP BY set). THE contract —
    // this component must not keep a second copy. Empty until the first preview
    // returns, so contract() below derives the same thing locally meanwhile.
    outputContract: { type: Object, default: () => ({}) },
    identifierColumns: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue', 'update:excludedColumns', 'update:identifierColumns', 'request-columns', 'refresh-sql', 'attribute-saved'],
  data() {
    return {
      dragOver: false,
      expandedStep: null,
      refsOpen: null,
      rightTab: 'output',
      libraryOpen: false,
      railFilter: '',
      editorOpen: false,
      editorDaId: null,
      editorKey: 0,
      addAfterCreate: false,
      sqlCache: {},
    };
  },
  computed: {
    nodes() { return this.modelValue.steps || []; },
    pkColumns() { return this.modelValue.primary_key_columns || []; },
    tabs() {
      const t = [{ key: 'output', label: 'Output' }];
      if (this.configTabLabel) t.push({ key: 'config', label: this.configTabLabel });
      return t;
    },
    attributesById() {
      return this.attributes.reduce((m, a) => { m[a.id] = a; return m; }, {});
    },
    railFilters() {
      const mk = (role, label) => {
        const r = ROLES[role] || ROLES.metadata;
        return { role, label, tx: role ? r.tx : '#4B5563', bg: role ? r.bg : '#F3F4F6', bd: role ? r.bd : '#E5E7EB', activeBg: role ? r.c : '#1F2937' };
      };
      return [mk('', 'All'), mk('date_spine', 'Date'), mk('population', 'Pop'), mk('denominator', 'Denom'), mk('intervention', 'Interv'), mk('fact', 'Fact')];
    },
    libraryItems() {
      const onCanvas = new Set(this.nodes.map((n) => n.da_id));
      const list = this.railFilter
        ? this.attributes.filter((a) => (a.role || '') === this.railFilter)
        : this.attributes;
      return list.slice().sort((a, b) => a.id - b.id).map((a) => ({ ...a, onCanvas: onCanvas.has(a.id) }));
    },
    allOutputColumns() {
      const cols = [];
      const seen = new Set();
      for (const node of this.nodes) {
        if (!node.da_id) continue;
        for (const col of this.columnsCache[node.da_id] || []) {
          if (!seen.has(col)) { seen.add(col); cols.push({ col, da_id: node.da_id }); }
        }
      }
      return cols.sort((a, b) => a.col.localeCompare(b.col));
    },
    activeColCount() {
      return this.allOutputColumns.filter((c) => !this.isExcluded(c.col)).length;
    },
    contract() {
      return this.outputContract || {};
    },
    contractChecks() {
      const active = new Set(
        this.allOutputColumns.filter((c) => !this.isExcluded(c.col)).map((c) => c.col.toUpperCase())
      );
      // ONE CONTRACT PER GRAIN, SERVED BY THE BACKEND. The engine's requirement is
      // its GROUP BY set (studio_engine NPI_GROUP_COLS / ENTITY_GROUP_COLS) plus
      // primary_key_columns — it does not care how many steps produced them, and it
      // detects the optional window/EFCTV columns by PRESENCE (has_efctv,
      // has_denom_efctv) and adapts. So there is no second shape to encode here.
      //
      // This list previously hardcoded the 4-role rate shape and demanded the
      // denominator/intervention window columns of every metric, which declared all
      // 38 live single-step metrics "incomplete" — 1558 and 1809 among them, both
      // serving a million precomputed rows.
      // primary_key_columns are already folded into contract().required.
      const requirements = this.contract.requirements || {};
      const checks = (this.contract.required || []).map((col) => ({
        col,
        required: true,
        reason: (requirements[col] || ['required']).join(', '),
      })).concat((this.contract.optional || []).map((col) => ({
        col,
        required: false,
        reason: 'optional',
      })));
      return checks.map((c) => {
        const present = active.has(c.col);
        let icon, iconColor, txColor;
        if (present) { icon = '✓'; iconColor = '#10B981'; txColor = '#374151'; }
        else if (c.required) { icon = '✗'; iconColor = '#EF4444'; txColor = '#B91C1C'; }
        else { icon = '○'; iconColor = '#D97706'; txColor = '#B45309'; }
        return { ...c, present, icon, iconColor, txColor };
      });
    },
    ready() {
      const req = this.contractChecks.filter((c) => c.required);
      const met = req.filter((c) => c.present).length;
      const missing = req.length - met;
      if (missing === 0) {
        return { icon: '✓', label: 'Ready to run', detail: 'All required contract columns present', tx: '#047857', bg: '#ECFDF5', bd: '#A7F3D0', dot: '#10B981', pct: 100 };
      }
      return {
        icon: '!',
        label: `${missing} contract gap${missing === 1 ? '' : 's'}`,
        detail: `${met} of ${req.length} required columns present`,
        tx: '#B91C1C', bg: '#FEF2F2', bd: '#FECACA', dot: '#EF4444',
        pct: Math.round((met / req.length) * 100),
      };
    },
  },
  methods: {
    identifierKey(column) {
      return column === 'YEAR' ? 'OBSERVATION_DT_YEAR' : column;
    },
    isIdentifier(column) {
      return (this.identifierColumns || []).includes(this.identifierKey(column));
    },
    toggleIdentifier(column) {
      const identifier = this.identifierKey(column);
      const current = [...(this.identifierColumns || [])];
      const next = current.includes(identifier)
        ? current.filter((value) => value !== identifier)
        : [...current, identifier];
      this.$emit('update:identifierColumns', next);
    },
    rm(node) { return ROLES[(node && node.role) || ''] || ROLES['']; },
    alias(i) { return String.fromCharCode(65 + i); },
    // The full join clause as one editable string: "<JOIN_TYPE> ON <predicate>"
    joinClause(node) {
      const jt = node.join_type || 'JOIN';
      const on = node.join_on_sql || '';
      return on ? `${jt} ON ${on}` : `${jt} ON `;
    },
    joinSummary(node) {
      const first = this.joinClause(node).split('\n').map((l) => l.trim()).filter(Boolean)[0];
      return first || '—';
    },
    refChips(stepIndex) {
      const chips = [];
      this.nodes.slice(0, stepIndex + 1).forEach((n, idx) => {
        const al = String.fromCharCode(97 + idx);
        const r = this.rm(n);
        (this.columnsCache[n.da_id] || []).forEach((col) => chips.push({ alias: al, col, c: r.c }));
      });
      return chips;
    },
    toggleStep(i) {
      this.expandedStep = this.expandedStep === i ? null : i;
      if (this.expandedStep === i) {
        const daId = this.nodes[i] && this.nodes[i].da_id;
        if (daId) this.fetchDaSql(daId);
      }
    },
    async fetchDaSql(daId, force = false) {
      if (this.sqlCache[daId] !== undefined && !force) return;
      try {
        const { data } = await axios.get(`api/impact_analytics/attributes/${daId}/`);
        this.sqlCache = { ...this.sqlCache, [daId]: data.sql_query || '-- (no SQL on this attribute)' };
      } catch (e) {
        this.sqlCache = { ...this.sqlCache, [daId]: `-- Failed to load SQL: ${e?.response?.data?.detail || e.message}` };
      }
    },

    isExcluded(col) { return (this.excludedColumns || []).includes(col); },
    toggleColumn(col) {
      const ex = (this.excludedColumns || []).slice();
      const idx = ex.indexOf(col);
      if (idx >= 0) ex.splice(idx, 1); else ex.push(col);
      this.$emit('update:excludedColumns', ex);
    },

    emitSteps(steps) {
      this.$emit('update:modelValue', { ...this.modelValue, steps });
    },

    async onDrop(e) {
      this.dragOver = false;
      const data = JSON.parse(e.dataTransfer.getData('text/plain') || '{}');
      if (data.da_id) await this.addAttribute(data.da_id);
    },
    async addAttribute(daId) {
      if (this.nodes.some((n) => n.da_id === daId)) { this.libraryOpen = false; return; }
      const attr = this.attributesById[daId];
      const role = attr?.role || '';
      this.$emit('request-columns', daId);
      const i = this.nodes.length;
      const node = { da_id: daId, role };
      if (i > 0) {
        node.join_type = role === 'intervention' ? 'LEFT JOIN' : 'JOIN';
        node.join_on_sql = this.defaultJoinOn(role, String.fromCharCode(97 + i));
      }
      this.emitSteps([...this.nodes, node]);
      this.libraryOpen = false;
      this.expandedStep = i;
    },
    defaultJoinOn(role, alias) {
      if (role === 'population') return `${alias}.PERFORMANCE_YEAR = a.year`;
      return `${alias}.BENE_MBI_ID = b.BENE_MBI_ID`;
    },
    // Parse the single clause string back into join_type + join_on_sql for the
    // engine (which emits "<join_type> <alias> ON <join_on_sql>").
    setJoinClause(i, raw) {
      const m = (raw || '').match(/^\s*(LEFT\s+JOIN|RIGHT\s+JOIN|INNER\s+JOIN|CROSS\s+JOIN|JOIN)\b\s*/i);
      let join_type = 'JOIN';
      let rest = raw || '';
      if (m) {
        join_type = m[1].toUpperCase().replace(/\s+/g, ' ');
        if (join_type === 'INNER JOIN') join_type = 'JOIN';
        rest = rest.slice(m[0].length);
      }
      // strip a leading ON (with or without it)
      rest = rest.replace(/^\s*ON\b\s*/i, '');
      const steps = this.nodes.map((n, idx) => (idx === i ? { ...n, join_type, join_on_sql: rest } : n));
      this.emitSteps(steps);
    },
    insertRef(i, token) {
      const cur = this.nodes[i].join_on_sql || '';
      const nv = cur && !/\s$/.test(cur) ? `${cur} ${token}` : cur + token;
      const steps = this.nodes.map((n, idx) => (idx === i ? { ...n, join_on_sql: nv } : n));
      this.emitSteps(steps);
    },
    removeNode(daId) {
      this.expandedStep = null;
      this.emitSteps(this.nodes.filter((n) => n.da_id !== daId));
    },
    // SQL can carry literal practice names, TINs/NPIs or ID lists, so copying
    // follows the Demo Mode copy policy.
    copySql() { navigator.clipboard.writeText(this.sql).catch(() => {}); },
    copyDaSql(daId) { navigator.clipboard.writeText(this.sqlCache[daId] || '').catch(() => {}); },

    // ---- DA edit / create drawer ----
    editAttribute(daId) {
      this.editorDaId = daId;
      this.addAfterCreate = false;
      this.editorKey += 1;
      this.editorOpen = true;
    },
    createAttribute() {
      this.editorDaId = null;       // create mode
      this.addAfterCreate = true;   // drop the new DA onto the canvas on save
      this.editorKey += 1;
      this.editorOpen = true;
    },
    onAttributeSaved(attr) {
      this.editorOpen = false;
      // bubble up so the host refreshes its attribute library + column cache
      this.$emit('attribute-saved', attr);
      // refresh this DA's columns (host owns columnsCache) + cached SQL
      if (attr && attr.id) {
        this.$emit('request-columns', attr.id);
        if (attr.sql_query !== undefined) this.sqlCache = { ...this.sqlCache, [attr.id]: attr.sql_query || '' };
        else this.fetchDaSql(attr.id, true);
      }
      // a freshly-created DA gets appended to the chain
      if (this.addAfterCreate && attr && attr.id && !this.nodes.some((n) => n.da_id === attr.id)) {
        this.$nextTick(() => this.addAttribute(attr.id));
      }
      this.addAfterCreate = false;
    },
  },
};
</script>

<style scoped>
.ms-builder :deep(.ms-scroll)::-webkit-scrollbar { width: 10px; height: 10px; }
.ms-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ms-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 10px; border: 3px solid transparent; background-clip: content-box; }
.ms-scroll::-webkit-scrollbar-track { background: transparent; }
@keyframes ms-rise { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ms-rise { animation: ms-rise 0.16s ease; }
@keyframes ms-drawer-in { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
.ms-drawer { animation: ms-drawer-in 0.22s cubic-bezier(0.2, 0.8, 0.2, 1); }
</style>
